import * as React from 'react'

/** Playback speed options */
export const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

/** Keyboard seek step in seconds (arrow keys) */
export const SEEK_STEP_SECONDS = 5

/** Volume slider step size */
export const VOLUME_STEP = 0.05

/** Progress bar handle size in pixels */
export const PROGRESS_HANDLE_SIZE_PX = 12

export function formatTime(seconds: number): string {
	const mins = Math.floor(seconds / 60)
	const secs = Math.floor(seconds % 60)
	return `${mins}:${secs.toString().padStart(2, '0')}`
}

export interface UseAudioPlayerProps {
	src: string
	audioRef: React.RefObject<HTMLAudioElement | null>
	onTimeUpdate?: (timeMs: number) => void
}

export interface UseAudioPlayerReturn {
	// State
	isPlaying: boolean
	currentTime: number
	duration: number
	playbackSpeed: number
	volume: number
	isMuted: boolean
	audioError: boolean
	progress: number
	// Refs (for progress bar DOM)
	progressRef: React.RefObject<HTMLDivElement | null>
	// Handlers
	togglePlay: () => void
	seekTo: (seconds: number) => void
	handleProgressClick: (e: React.MouseEvent<HTMLDivElement>) => void
	handleProgressKeyDown: (e: React.KeyboardEvent) => void
	handleSpeedChange: (speed: number) => void
	toggleMute: () => void
	handleVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	clearErrorAndRetry: () => void
}

export function useAudioPlayer({
	src,
	audioRef,
	onTimeUpdate,
}: UseAudioPlayerProps): UseAudioPlayerReturn {
	const [isPlaying, setIsPlaying] = React.useState(false)
	const [currentTime, setCurrentTime] = React.useState(0)
	const [duration, setDuration] = React.useState(0)
	const [playbackSpeed, setPlaybackSpeed] = React.useState(1)
	const [volume, setVolume] = React.useState(1)
	const [isMuted, setIsMuted] = React.useState(false)
	const [audioError, setAudioError] = React.useState(false)

	const progressRef = React.useRef<HTMLDivElement>(null)

	const onTimeUpdateRef = React.useRef(onTimeUpdate)
	onTimeUpdateRef.current = onTimeUpdate

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional -- reset error when src prop changes
	React.useEffect(() => {
		setAudioError(false)
	}, [src])

	React.useEffect(() => {
		const audio = audioRef.current
		if (!audio) return

		const handlePlay = () => setIsPlaying(true)
		const handlePause = () => setIsPlaying(false)
		const handleEnded = () => setIsPlaying(false)
		const handleTimeUpdate = () => {
			setCurrentTime(audio.currentTime)
			onTimeUpdateRef.current?.(audio.currentTime * 1000)
		}
		const handleLoadedMetadata = () => {
			setDuration(audio.duration)
			setAudioError(false)
		}
		const handleVolumeChange = () => {
			setVolume(audio.volume)
			setIsMuted(audio.muted)
		}
		const handleError = () => setAudioError(true)

		audio.addEventListener('play', handlePlay)
		audio.addEventListener('pause', handlePause)
		audio.addEventListener('ended', handleEnded)
		audio.addEventListener('timeupdate', handleTimeUpdate)
		audio.addEventListener('loadedmetadata', handleLoadedMetadata)
		audio.addEventListener('volumechange', handleVolumeChange)
		audio.addEventListener('error', handleError)

		if (audio.duration) setDuration(audio.duration)
		setVolume(audio.volume)
		setIsMuted(audio.muted)

		return () => {
			audio.removeEventListener('play', handlePlay)
			audio.removeEventListener('pause', handlePause)
			audio.removeEventListener('ended', handleEnded)
			audio.removeEventListener('timeupdate', handleTimeUpdate)
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
			audio.removeEventListener('volumechange', handleVolumeChange)
			audio.removeEventListener('error', handleError)
		}
	}, [audioRef])

	const togglePlay = React.useCallback(() => {
		const audio = audioRef.current
		if (!audio) return
		if (isPlaying) audio.pause()
		else audio.play()
	}, [audioRef, isPlaying])

	const seekTo = React.useCallback(
		(seconds: number) => {
			const audio = audioRef.current
			if (!audio || !duration || !Number.isFinite(duration)) return
			audio.currentTime = Math.max(0, Math.min(seconds, duration))
		},
		[audioRef, duration]
	)

	const handleProgressClick = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const audio = audioRef.current
			const progressBar = progressRef.current
			if (!audio || !progressBar || !duration || !Number.isFinite(duration)) return
			const rect = progressBar.getBoundingClientRect()
			const percent = (e.clientX - rect.left) / rect.width
			audio.currentTime = Math.max(0, Math.min(percent * duration, duration))
		},
		[audioRef, duration]
	)

	const handleProgressKeyDown = React.useCallback(
		(e: React.KeyboardEvent) => {
			const audio = audioRef.current
			if (!audio || !duration || !Number.isFinite(duration)) return
			switch (e.key) {
				case 'ArrowLeft':
					e.preventDefault()
					seekTo(audio.currentTime - SEEK_STEP_SECONDS)
					break
				case 'ArrowRight':
					e.preventDefault()
					seekTo(audio.currentTime + SEEK_STEP_SECONDS)
					break
				case 'Home':
					e.preventDefault()
					seekTo(0)
					break
				case 'End':
					e.preventDefault()
					seekTo(duration)
					break
			}
		},
		[audioRef, duration, seekTo]
	)

	const handleSpeedChange = React.useCallback(
		(speed: number) => {
			const audio = audioRef.current
			if (!audio) return
			audio.playbackRate = speed
			setPlaybackSpeed(speed)
		},
		[audioRef]
	)

	const toggleMute = React.useCallback(() => {
		const audio = audioRef.current
		if (!audio) return
		audio.muted = !audio.muted
	}, [audioRef])

	const handleVolumeChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const audio = audioRef.current
			if (!audio) return
			const newVolume = Number.parseFloat(e.target.value)
			audio.volume = newVolume
			if (newVolume > 0 && audio.muted) audio.muted = false
		},
		[audioRef]
	)

	const clearErrorAndRetry = React.useCallback(() => {
		setAudioError(false)
		audioRef.current?.load()
	}, [audioRef])

	const progress = duration > 0 ? (currentTime / duration) * 100 : 0

	return {
		isPlaying,
		currentTime,
		duration,
		playbackSpeed,
		volume,
		isMuted,
		audioError,
		progress,
		progressRef,
		togglePlay,
		seekTo,
		handleProgressClick,
		handleProgressKeyDown,
		handleSpeedChange,
		toggleMute,
		handleVolumeChange,
		clearErrorAndRetry,
	}
}
